/**
 * DI-3-F — the ledger: which blobs of a set are committed (story §2.1
 * `ledger.ts`; locked resume-by-ledger-then-by-ingest-errors; N8).
 *
 * The SAS is write-only (`cw`): the browser cannot HEAD or list a blob, so
 * the ONLY local truth about what is up there is this record — one entry per
 * blob whose Put Block List (or Put Blob) answered 201, kept in the set's
 * session record (IndexedDB, `ClusterIntake.upload.ledger`). A resume (a new
 * tab, a reload, a crash — a new runner over the same record) skips every
 * file the ledger names with the same size and MD5 and sends the rest from
 * block 0. The server's verification is the final word: a file the ingest
 * reports `blob_absent` / `blob_mismatch` leaves the ledger (`evict`) and is
 * sent again. Small on purpose: file name → three fields.
 *
 * DI-3-S9 (smoke F8) — a THIRD input, on a resume only: before the first
 * block, the server names the originals it KNOWS it lacks (`listIntakeBlobs?
 * state=missing`, `api/intake.ts:fetchMissingBlobs` — an ingest verified them
 * absent or wrong) and `reconcile` drops exactly those entries. It never adds
 * one (locked resume-by-ledger-then-by-ingest-errors), and an entry the server
 * has not verified stays (DI-3-S4-FE: resume skips what the ledger marks
 * sent). A store that lost blobs before any ingest checked them is still
 * caught by the ingest's `evict` after the pass (the safety net).
 */

export interface LedgerEntry {
  sizeBytes: number;
  md5: string;
  committedAt: string;
}

export type Ledger = Readonly<Record<string, LedgerEntry>>;

export interface LedgerFile {
  filename: string;
  sizeBytes: number;
  md5Hex: string;
}

/** Is `f` committed (same name, size and MD5)? */
export function isCommitted(ledger: Ledger, f: LedgerFile): boolean {
  const e = ledger[f.filename];
  return (
    e?.sizeBytes === f.sizeBytes &&
    e.md5.toLowerCase() === f.md5Hex.toLowerCase()
  );
}

/** The files still to send. */
export function pendingFiles<T extends LedgerFile>(
  ledger: Ledger,
  files: readonly T[],
): T[] {
  return files.filter((f) => !isCommitted(ledger, f));
}

export function commit(ledger: Ledger, f: LedgerFile, at: string): Ledger {
  return {
    ...ledger,
    [f.filename]: { sizeBytes: f.sizeBytes, md5: f.md5Hex, committedAt: at },
  };
}

/** The ingest found these blobs absent or wrong: they are sent again. */
export function evict(ledger: Ledger, filenames: Iterable<string>): Ledger {
  const gone = new Set(filenames);
  return Object.fromEntries(
    Object.entries(ledger).filter(([name]) => !gone.has(name)),
  );
}

/**
 * DI-3-S9 — the ledger minus every entry the server marks `missing`
 * (`missing`: the names `listIntakeBlobs?state=missing` returned). Pure; an
 * entry is never added; an entry the server has not named (verified present,
 * or never checked) is kept as it is.
 */
export function reconcile(
  ledger: Ledger,
  missing: ReadonlySet<string>,
): Ledger {
  return Object.fromEntries(
    Object.entries(ledger).filter(([name]) => !missing.has(name)),
  );
}

/** Bytes of `files` the ledger holds. */
export function committedBytes(
  ledger: Ledger,
  files: readonly LedgerFile[],
): number {
  let n = 0;
  for (const f of files) if (isCommitted(ledger, f)) n += f.sizeBytes;
  return n;
}
